/**
 * --- ULTRA ROBUST MEGA MENU CONTROLLER ---
 * Height-only animation (NO opacity)
 * Fixes first-time hover issue
 */
(function () {
  let initialized = false;
  let megaMenuItems = new WeakSet();

  const initMegaMenu = () => {
    const headerMenu =
      document.querySelector('header-menu') ||
      document.querySelector('[header-menu]');
    if (!headerMenu) {
      console.log('⚠️ Header menu not found, retrying...');
      return;
    }

    const menuItems = headerMenu.querySelectorAll('.menu-list__list-item');

    /* ---------------- HEIGHT CALCULATION ---------------- */
    const updateMegaHeight = (item) => {
      const grid = item.querySelector('.mega-menu__grid');
      const submenu = item.querySelector('.menu-list__submenu');
      if (!grid || !submenu) return;

      grid.style.minHeight = '0';

      requestAnimationFrame(() => {
        let maxHeight = 0;

        const baseList = grid.querySelector('.mega-menu__list');
        if (baseList) maxHeight = baseList.scrollHeight;

        grid
          .querySelectorAll('[data-mega-menu-container].is-active')
          .forEach((c) => {
            const list = c.querySelector('.mega-menu__nested-list');
            if (list) maxHeight = Math.max(maxHeight, list.scrollHeight);
          });

        grid
          .querySelectorAll(
            '.mega-menu__featured-pages, .mega-menu__featured-content'
          )
          .forEach((el) => {
            maxHeight = Math.max(maxHeight, el.scrollHeight);
          });

        if (maxHeight > 0) {
          const h = `${maxHeight}px`;
          headerMenu.style.setProperty('--submenu-height', h, 'important');
          submenu.style.setProperty('--submenu-height', h, 'important');
          grid.style.minHeight = h;
        }
      });
    };

    /* ---------------- ACTIVE STATE ---------------- */
    const setActiveState = (link, forceChild = true) => {
      if (!link) return;

      const level = parseInt(link.dataset.level, 10);
      const grid = link.closest('.mega-menu__grid');
      if (!grid) return;

      grid
        .querySelectorAll(`[data-mega-menu-link][data-level="${level}"]`)
        .forEach((l) => {
          l.classList.remove('is-active');
          l.setAttribute('aria-expanded', 'false');
        });

      link.classList.add('is-active');
      link.setAttribute('aria-expanded', 'true');

      const next = level + 1;
      const parent = link.closest('div');
      const container = parent?.querySelector(
        `[data-mega-menu-container][data-level="${next}"]`
      );

      grid
        .querySelectorAll(`[data-mega-menu-container][data-level="${next}"]`)
        .forEach((c) => {
          if (c !== container) {
            c.classList.remove('is-active');
            c.style.display = 'none';
          }
        });

      if (container) {
        container.classList.add('is-active');
        container.style.display = 'block';

        if (forceChild) {
          const children = container.querySelectorAll('[data-mega-menu-link]');
          if (children.length) {
            const first =
              [...children].find((c) =>
                c.closest('div')?.querySelector('[data-mega-menu-container]')
              ) || children[0];
            if (first && !first.classList.contains('is-active')) {
              setActiveState(first, true);
            }
          }
        }
      }

      updateMegaHeight(grid.closest('.menu-list__list-item'));
    };

    /* ---------------- RESET ---------------- */
    const resetMega = (item) => {
      item.querySelectorAll('.is-active').forEach((el) => {
        el.classList.remove('is-active');
        if (el.hasAttribute('aria-expanded')) {
          el.setAttribute('aria-expanded', 'false');
        }
      });

      item.querySelectorAll('[data-mega-menu-container]').forEach((c) => {
        c.classList.remove('is-active');
        c.style.display = 'none';
      });

      const grid = item.querySelector('.mega-menu__grid');
      if (grid) grid.style.minHeight = '';
    };

    /* ---------------- BIND EVENTS ---------------- */
    menuItems.forEach((item) => {
      const mega = item.querySelector('.mega-menu');
      if (!mega) return;

      if (megaMenuItems.has(item)) return;
      megaMenuItems.add(item);

      const link = item.querySelector('.menu-list__link');
      const submenu = item.querySelector('.menu-list__submenu');
      const megaLinks = item.querySelectorAll('[data-mega-menu-link]');

      let closeTimer = null;
      let heightTimer = null;
      let isOpen = false;

      /* ---------- OPEN ---------- */
      const openMega = () => {
        clearTimeout(closeTimer);
        clearInterval(heightTimer);

        isOpen = true;

        headerMenu.style.setProperty('--submenu-height-lock', 'true');
        headerMenu.dataset.megaMenuActive = 'true';

        link?.setAttribute('aria-expanded', 'true');
        headerMenu.setAttribute('aria-expanded', 'true');

        document
          .querySelector('.header')
          ?.classList.add('open-max-mega-menu');

        const grid = item.querySelector('.mega-menu__grid');
        if (grid) {
          const activeL2 = grid.querySelector(
            '[data-mega-menu-link][data-level="2"].is-active'
          );

          if (!activeL2) {
            const all = [...grid.querySelectorAll('[data-level="2"]')];
            const start =
              all.find((l) =>
                l.closest('div')?.querySelector('[data-mega-menu-container]')
              ) || all[0];
            if (start) setActiveState(start, true);
          } else {
            updateMegaHeight(item);
          }
        }

        /* --- AGGRESSIVE HEIGHT REASSERTION --- */
        let reassertCount = 0;
        heightTimer = setInterval(() => {
          reassertCount++;

          if (
            !isOpen ||
            (!item.matches(':hover') && !submenu?.matches(':hover'))
          ) {
            clearInterval(heightTimer);
            return;
          }

          if (
            headerMenu.style.getPropertyValue('--submenu-height-lock') !==
            'true'
          ) {
            headerMenu.style.setProperty('--submenu-height-lock', 'true');
          }

          updateMegaHeight(item);

          if (reassertCount > 25) {
            clearInterval(heightTimer);
            heightTimer = setInterval(() => {
              if (
                isOpen &&
                (item.matches(':hover') || submenu?.matches(':hover'))
              ) {
                updateMegaHeight(item);
              }
            }, 1000);
          }
        }, 200);
      };

      /* ---------- CLOSE ---------- */
      const closeMega = (e) => {
        if (
          e?.relatedTarget &&
          (item.contains(e.relatedTarget) ||
            submenu?.contains(e.relatedTarget))
        ) {
          return;
        }

        if (item.matches(':hover') || submenu?.matches(':hover')) return;

        isOpen = false;
        clearInterval(heightTimer);

        headerMenu.style.removeProperty('--submenu-height-lock');
        delete headerMenu.dataset.megaMenuActive;

        link?.setAttribute('aria-expanded', 'false');
        headerMenu.setAttribute('aria-expanded', 'false');
        headerMenu.style.setProperty('--submenu-height', '0px');

        document
          .querySelector('.header')
          ?.classList.remove('open-max-mega-menu');

        resetMega(item);
      };

      /* ---------- EVENTS ---------- */
      item.addEventListener('mouseenter', openMega, true);
      item.addEventListener(
        'mouseleave',
        (e) => {
          closeTimer = setTimeout(() => closeMega(e), 400);
        },
        true
      );

      submenu?.addEventListener(
        'mouseenter',
        () => clearTimeout(closeTimer),
        true
      );
      submenu?.addEventListener(
        'mouseleave',
        (e) => {
          closeTimer = setTimeout(() => closeMega(e), 400);
        },
        true
      );

      megaLinks.forEach((l) => {
        l.addEventListener(
          'mouseenter',
          () => setActiveState(l, true),
          true
        );
      });
    });

    initialized = true;
  };

  /* ---------------- INIT LOGIC ---------------- */
  const tryInit = () => {
    if (initialized) return;

    if (document.readyState !== 'loading') {
      initMegaMenu();
      setTimeout(initMegaMenu, 50);
      setTimeout(initMegaMenu, 200);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', tryInit);
  } else {
    tryInit();
  }

  window.addEventListener('load', tryInit);

  ['shopify:section:load', 'shopify:section:reorder'].forEach((e) => {
    document.addEventListener(e, () => {
      initialized = false;
      setTimeout(tryInit, 50);
    });
  });

  ['page:loaded', 'turbo:load'].forEach((e) => {
    document.addEventListener(e, () => {
      initialized = false;
      setTimeout(tryInit, 50);
    });
  });

  /* ---------------- THEME OVERRIDE SAFETY ---------------- */
  const originalDefine = customElements.define;
  customElements.define = function (name, constructor, options) {
    if (name === 'header-menu') {
      class MegaMenuSafeHeaderMenu extends constructor {
        activate(event) {
          const target = event?.target;
          if (
            target &&
            target
              .closest('.menu-list__list-item')
              ?.querySelector('.mega-menu')
          ) {
            return;
          }
          return super.activate?.(event);
        }

        deactivate(event) {
          const target = event?.target;
          if (
            target &&
            target
              .closest('.menu-list__list-item')
              ?.querySelector('.mega-menu')
          ) {
            return;
          }
          return super.deactivate?.(event);
        }
      }
      return originalDefine.call(this, name, MegaMenuSafeHeaderMenu, options);
    }
    return originalDefine.call(this, name, constructor, options);
  };
})();
